/**
 * Currency Converter Utility
 * Handles conversion from base currency (EUR) to target currencies
 */

// Currency mapping by country code
export const CURRENCY_BY_COUNTRY: Record<string, string> = {
    IN: 'INR',
    US: 'USD',
    GB: 'GBP',
    CA: 'CAD',
    AU: 'AUD',
    JP: 'JPY',
    CN: 'CNY',
    TH: 'THB',
    ID: 'IDR',
    VN: 'VND',
    KR: 'KRW',
    MY: 'MYR',
    HK: 'HKD',
    PH: 'PHP',
    SG: 'SGD',
    AE: 'AED',
    DE: 'EUR',
    FR: 'EUR',
    IT: 'EUR',
    ES: 'EUR',
    NL: 'EUR',
    BE: 'EUR',
    AT: 'EUR',
    PT: 'EUR',
    IE: 'EUR'
}

/**
 * Common country names (English + German, lower-cased and diacritic-stripped)
 * mapped to their ISO 3166-1 alpha-2 code. Used to normalize free-text country
 * input — the property form stores whatever the host types ("Germany",
 * "Deutschland", …) — so currency resolution does not silently no-op on a
 * non-alpha-2 value. Covers the countries we currently price for.
 */
export const COUNTRY_NAME_TO_ISO2: Record<string, string> = {
    germany: 'DE',
    deutschland: 'DE',
    austria: 'AT',
    osterreich: 'AT',
    france: 'FR',
    frankreich: 'FR',
    italy: 'IT',
    italien: 'IT',
    italia: 'IT',
    spain: 'ES',
    spanien: 'ES',
    espana: 'ES',
    netherlands: 'NL',
    'the netherlands': 'NL',
    niederlande: 'NL',
    holland: 'NL',
    belgium: 'BE',
    belgien: 'BE',
    belgique: 'BE',
    portugal: 'PT',
    ireland: 'IE',
    irland: 'IE',
    'united states': 'US',
    'united states of america': 'US',
    usa: 'US',
    'vereinigte staaten': 'US',
    'united kingdom': 'GB',
    uk: 'GB',
    'great britain': 'GB',
    grossbritannien: 'GB',
    england: 'GB',
    canada: 'CA',
    kanada: 'CA',
    australia: 'AU',
    australien: 'AU',
    japan: 'JP',
    china: 'CN',
    thailand: 'TH',
    indonesia: 'ID',
    indonesien: 'ID',
    vietnam: 'VN',
    'south korea': 'KR',
    sudkorea: 'KR',
    korea: 'KR',
    malaysia: 'MY',
    'hong kong': 'HK',
    philippines: 'PH',
    philippinen: 'PH',
    singapore: 'SG',
    singapur: 'SG',
    'united arab emirates': 'AE',
    uae: 'AE',
    'vereinigte arabische emirate': 'AE',
    india: 'IN',
    indien: 'IN'
}

/**
 * Normalizes free-text country input to an ISO 3166-1 alpha-2 code.
 * Returns the mapped code for a known country name (case/diacritic-insensitive),
 * an already-2-letter input upper-cased, or null when it cannot be resolved.
 */
export function normalizeCountryToIso2(value: string | null | undefined): string | null {
    if (!value) return null
    const trimmed = value.trim()
    if (!trimmed) return null
    const normalized = trimmed
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    // Prefer the name map first so aliases that happen to be 2 letters (e.g.
    // "uk" -> GB) resolve correctly instead of being treated as a literal code.
    if (COUNTRY_NAME_TO_ISO2[normalized]) return COUNTRY_NAME_TO_ISO2[normalized]
    if (trimmed.length === 2) return trimmed.toUpperCase()
    return null
}

export const CURRENCY_SYMBOLS: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    INR: '₹',
    JPY: '¥',
    CNY: '¥',
    THB: '฿',
    IDR: 'Rp',
    VND: '₫',
    KRW: '₩',
    MYR: 'RM',
    HKD: 'HK$',
    PHP: '₱',
    CAD: '$',
    AUD: '$',
    SGD: '$',
    AED: 'د.إ'
}

/** Kurzbezeichnungen für UI (Select, Tooltips) */
export const CURRENCY_LABELS: Record<string, string> = {
    EUR: 'Euro',
    USD: 'US Dollar',
    GBP: 'British Pound',
    INR: 'Indian Rupee',
    JPY: 'Japanese Yen',
    CNY: 'Chinese Yuan',
    THB: 'Thai Baht',
    IDR: 'Indonesian Rupiah',
    VND: 'Vietnamese Dong',
    KRW: 'South Korean Won',
    MYR: 'Malaysian Ringgit',
    HKD: 'Hong Kong Dollar',
    PHP: 'Philippine Peso',
    CAD: 'Canadian Dollar',
    AUD: 'Australian Dollar',
    SGD: 'Singapore Dollar',
    AED: 'UAE Dirham'
}

/** Display order for currency selectors; must cover every Prisma Currency enum value exactly once. */
export const CURRENCY_CODES_SUPPORTED = [
    'EUR',
    'USD',
    'GBP',
    'INR',
    'JPY',
    'CNY',
    'THB',
    'IDR',
    'VND',
    'KRW',
    'MYR',
    'HKD',
    'PHP',
    'CAD',
    'AUD',
    'SGD',
    'AED'
] as const

export interface ExchangeRates {
    base: string
    date: string
    rates: Record<string, number>
}

export async function fetchExchangeRates(): Promise<ExchangeRates> {
    try {
        if (typeof window === 'undefined') {
            const { getExchangeRates } = await import('@/lib/utils/exchange-rate-service')
            return await getExchangeRates()
        }

        const response = await fetch('/api/exchange-rates')

        if (!response.ok) {
            throw new Error('Failed to load exchange rates')
        }

        return (await response.json()) as ExchangeRates
    } catch (error) {
        console.error('Error fetching exchange rates:', error)
        return getDefaultRates()
    }
}

/**
 * Default exchange rates as fallback when live rates are unavailable.
 * We intentionally default to 1:1 conversions so we don't display stale
 * or misleading prices. Downstream consumers can decide how to surface
 * this scenario to users if needed.
 */
export function getDefaultRates(): ExchangeRates {
    const rates = CURRENCY_CODES_SUPPORTED.reduce<Record<string, number>>((acc, code) => {
        acc[code] = 1
        return acc
    }, {})

    return {
        base: 'EUR',
        date: new Date().toISOString(),
        rates
    }
}

/**
 * Converts price from EUR to target currency
 * @param amount - Amount in EUR
 * @param targetCurrency - Target currency code (e.g., 'INR', 'USD')
 * @param exchangeRates - Exchange rates object
 * @returns Converted amount
 */
export function convertCurrency(amount: number, targetCurrency: string, exchangeRates: ExchangeRates): number {
    if (targetCurrency === 'EUR') {
        return amount
    }

    const rate = exchangeRates.rates[targetCurrency]
    if (!rate) {
        console.warn(`Exchange rate not found for ${targetCurrency}, using EUR`)
        return amount
    }

    return amount * rate
}

/**
 * Formats currency amount with proper locale and symbol
 * @param amount - Amount to format
 * @param currency - Currency code
 * @param locale - Locale for formatting (defaults to currency's locale)
 * @returns Formatted currency string with 2 decimals
 */
export function formatCurrency(amount: number, currency: string, locale?: string): string {
    const currencyLocale = locale || getCurrencyLocale(currency)

    try {
        return new Intl.NumberFormat(currencyLocale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount)
    } catch {
        const symbol = CURRENCY_SYMBOLS[currency] || currency
        const formattedAmount = amount.toLocaleString(currencyLocale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })
        return `${symbol}${formattedAmount}`
    }
}

function getCurrencyLocale(currency: string): string {
    const localeMap: Record<string, string> = {
        EUR: 'de-DE',
        USD: 'en-US',
        GBP: 'en-GB',
        INR: 'en-IN',
        JPY: 'ja-JP',
        CNY: 'zh-CN',
        THB: 'th-TH',
        IDR: 'id-ID',
        VND: 'vi-VN',
        KRW: 'ko-KR',
        MYR: 'ms-MY',
        HKD: 'zh-HK',
        PHP: 'en-PH',
        CAD: 'en-CA',
        AUD: 'en-AU',
        SGD: 'en-SG',
        AED: 'ar-AE'
    }
    return localeMap[currency] || 'en-US'
}

/**
 * Gets currency code from country code
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns Currency code (e.g., 'INR', 'USD')
 */
export function getCurrencyFromCountry(countryCode: string): string {
    return CURRENCY_BY_COUNTRY[countryCode.toUpperCase()] || 'EUR'
}
